import React, { useState } from "react";
import {
  Target,
  Calendar,
  DollarSign,
  FileText,
  Users,
  Briefcase,
  Clock,
  BarChart3,
  Lock,
} from "lucide-react";
import {
  colors as DS,
  TechLabel,
  StatusBadge,
  PageContainer,
  Section,
  SectionHeader,
  PrimaryButton,
  BlueprintCard,
  SkeuInput,
  SkeuCheckbox,
} from "../design-system";

const ACCENT = DS.product.hrms; // teal
const WEBIDEAL_LOGO = '/logofinal.png';

// ── Remember-me password vault (AES-256-GCM via Web Crypto) ──────────────
// Why not just hash it: a hash can't be reversed, so it can never be put
// back into the password field, hashing and "recoverable for autofill"
// are mutually exclusive. This is real symmetric encryption instead.
//
// The AES key is generated with `extractable: false` and stored directly
// as a CryptoKey object in IndexedDB, its raw bytes are never exposed to
// JS, not even to this code. localStorage only ever holds ciphertext + IV,
// both meaningless without that key.
//
// ⚠️ Honest limit, not a bug: this defeats a casual localStorage glance,
// browser extensions/log tools scanning storage for password-shaped
// strings, and copy-paste harvesting. It does NOT defeat an XSS bug on
// this exact origin, malicious same-origin JS can call this same
// decryptStoredPassword() function and get the plaintext back, because a
// page that can decrypt its own remembered password for autofill is, by
// definition, also code an attacker's injected script could invoke. No
// client-side-only scheme can close that gap; only the OS/browser's own
// password manager (which JS can never read) truly can. That's a
// fundamental property of storing a recoverable secret in the browser,
// not something more code here can fix.
const REMEMBER_DB_NAME = "hrms_secure_store";
const REMEMBER_STORE_NAME = "keys";
const REMEMBER_KEY_ID = "remember_me_key";

function openRememberDb() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) return reject(new Error("IndexedDB unavailable"));
    const req = window.indexedDB.open(REMEMBER_DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(REMEMBER_STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getOrCreateRememberKey() {
  const db = await openRememberDb();
  const existing = await new Promise((resolve, reject) => {
    const tx = db.transaction(REMEMBER_STORE_NAME, "readonly");
    const req = tx.objectStore(REMEMBER_STORE_NAME).get(REMEMBER_KEY_ID);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
  if (existing) return existing;

  const key = await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false, // non-extractable, raw key bytes can never be read out, by anyone
    ["encrypt", "decrypt"]
  );
  await new Promise((resolve, reject) => {
    const tx = db.transaction(REMEMBER_STORE_NAME, "readwrite");
    const req = tx.objectStore(REMEMBER_STORE_NAME).put(key, REMEMBER_KEY_ID);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  return key;
}

function bufToB64(buf) {
  return window.btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function b64ToBuf(b64) {
  const bin = window.atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

async function encryptStoredPassword(plaintext) {
  try {
    const key = await getOrCreateRememberKey();
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const data = new TextEncoder().encode(plaintext);
    const cipherBuf = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
    return `${bufToB64(iv)}.${bufToB64(cipherBuf)}`;
  } catch {
    return null; // crypto/IndexedDB unavailable, caller should skip storing
  }
}

async function decryptStoredPassword(stored) {
  try {
    const [ivB64, cipherB64] = stored.split(".");
    const key = await getOrCreateRememberKey();
    const iv = new Uint8Array(b64ToBuf(ivB64));
    const plainBuf = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, b64ToBuf(cipherB64));
    return new TextDecoder().decode(plainBuf);
  } catch {
    return ""; // corrupt/missing/undecryptable, fail quiet, just don't fill it
  }
}

// Small helper: a labelled skeuomorphic field matching the design system.
function Field({ label, ...rest }) {
  return (
    <div>
      <TechLabel as="label" style={{ color: `${DS.grid}99` }} className="block mb-1.5">
        {label}
      </TechLabel>
      <SkeuInput {...rest} />
    </div>
  );
}

const featureTools = [
  { icon: Users, title: "Centralized Employees", desc: "One source of truth for people data, profiles and documents." },
  { icon: Calendar, title: "Attendance & Leave", desc: "Automate logs, shifts, approvals and balances." },
  { icon: DollarSign, title: "Payroll Automation", desc: "Accurate tax and compliance calculations, every cycle." },
  { icon: FileText, title: "Expense Management", desc: "Track, approve and reconcile claims in one place." },
  { icon: Target, title: "Performance Reviews", desc: "Goals, appraisals and continuous progress tracking." },
  { icon: Briefcase, title: "Recruitment Tracker", desc: "Move candidates through a clear, structured pipeline." },
  { icon: Clock, title: "Timesheets", desc: "Capture billable and project hours with zero friction." },
  { icon: BarChart3, title: "HR Analytics", desc: "Make data-driven decisions with live workforce insight." },
  { icon: Lock, title: "Self-Service Access", desc: "Employees manage their own info, safely and simply." },
];

export default function LandingPage({
  navigatePage,
  // LOGIN PROPS
  loginEmail,
  setLoginEmail,
  loginPassword,
  setLoginPassword,
  handleLoginSubmit,
  rememberMe,
  setRememberMe,
  // SIGNUP PROPS
  signupUsername,
  setSignupUsername,
  signupEmail,
  setSignupEmail,
  signupPassword,
  setSignupPassword,
  confirmPassword,
  setConfirmPassword,
  handleSignupSubmit,
  signupRequestSent,
  setSignupRequestSent,
  // REQUEST-ACCESS PROPS
  requestName,
  setRequestName,
  requestEmail,
  setRequestEmail,
  handleRequestAccess,
}) {
  const [mode, setMode] = useState("signin"); // 'signin' | 'signup' | 'request'
  const [requestSent, setRequestSent] = useState(false);

  // ── "Remember me" (frontend half) ────────────────────────────────
  // Session persistence itself lives entirely on the backend: an httpOnly
  // refresh-token cookie this page never sees or touches (api/backendApi.js).
  // This block ONLY controls whether the sign-in form re-fills itself.
  // See the encryptStoredPassword/decryptStoredPassword functions above
  // for what protection this does and doesn't provide.
  React.useEffect(() => {
    const rememberedEmail = window.localStorage.getItem("hrms_remembered_email");
    const storedCipher = window.localStorage.getItem("hrms_remembered_password");
    if (rememberedEmail) {
      setLoginEmail?.(rememberedEmail);
      setRememberMe?.(true);
    }
    if (storedCipher) {
      decryptStoredPassword(storedCipher).then((decoded) => {
        if (decoded) setLoginPassword?.(decoded);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitLogin = async (e) => {
    e.preventDefault();
    const success = await handleLoginSubmit();
    if (!success) return; // failed/blocked login, leave any prior remember-me state untouched

    if (rememberMe) {
      window.localStorage.setItem("hrms_remembered_email", loginEmail);
      const cipher = await encryptStoredPassword(loginPassword);
      if (cipher) {
        window.localStorage.setItem("hrms_remembered_password", cipher);
      } else {
        window.localStorage.removeItem("hrms_remembered_password"); // encryption unavailable, don't fall back to plaintext
      }
    } else {
      window.localStorage.removeItem("hrms_remembered_email");
      window.localStorage.removeItem("hrms_remembered_password");
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    const ok = await handleRequestAccess?.();
    if (ok) setRequestSent(true);
  };

  const goToSignin = () => {
    setSignupRequestSent?.(false);
    setMode("signin");
  };

  const eyebrow =
    mode === "signup"
      ? "create account"
      : mode === "request"
      ? "request access"
      : "secure sign in";

  return (
    <div
      className="min-h-screen"
      style={{
        background: DS.paper,
        color: DS.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
          >
            {/* NAV */}
      <nav
        className="flex items-center justify-between px-6 md:px-10 py-4 border-b"
        style={{ borderColor: DS.hairline, background: "rgba(247,247,245,0.9)" }}
      >
        <div className="flex items-center gap-3">
          <img
            src={WEBIDEAL_LOGO}
            alt="WebIdeal HRMS"
            className="w-9 h-9 object-contain"
          />
          <div>
            <span className="text-lg font-extrabold tracking-tight">WebIdeal</span>
            <TechLabel className="ml-2" style={{ color: ACCENT }}>module · hrms</TechLabel>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-8">
          {["Features", "Integrations", "About", "Contact"].map((l) => (
            <button key={l} className="text-sm font-semibold" style={{ color: DS.grid }}>
              {l}
            </button>
          ))}
        </div>
      </nav>
        
      {/* HERO + AUTH */}
      <Section className="!py-16 md:!py-20" style={{ background: "transparent" }}>
        <PageContainer>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* LEFT, pitch */}
            <div className="max-w-xl">
              <StatusBadge color={ACCENT}>people operations</StatusBadge>
              <h1
                className="mt-5 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight"
                style={{ color: DS.forest }}
              >
                HR software for smarter people operations
              </h1>
              <p className="mt-5 text-lg" style={{ color: DS.grid }}>
                Simplify and automate every HR process, from onboarding to payroll, in one intuitive platform designed to empower your workforce.
              </p>

              <ul className="mt-8 space-y-3">
                {[
                  "Employee Management, centralize data, profiles and documents.",
                  "Attendance & Leave, automate logs, shifts and approvals.",
                  "Payroll, accurate tax and compliance calculations.",
                  "Performance, goals, reviews and progress tracking.",
                  "Analytics, make data-driven HR decisions.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm" style={{ color: DS.forest }}>
                    <span className="w-2 h-2 mt-1.5 flex-shrink-0" style={{ background: ACCENT }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* RIGHT, auth card */}
            <div className="w-full flex lg:justify-end">
              <BlueprintCard accent={ACCENT} className="w-full max-w-[440px]" spatial>
                <TechLabel style={{ color: `${DS.grid}99` }}>{eyebrow}</TechLabel>

                {mode === "signin" && (
                  <>
                    <h2 className="mt-2 text-2xl font-extrabold" style={{ color: DS.forest }}>
                      Sign in
                    </h2>
                    <form
                      className="mt-6 space-y-4"
                      onSubmit={submitLogin}
                      autoComplete="off"
                    >
                      {/* Decoy fields: Chrome/Edge/Firefox scan a form for the
                          first visible username+password pair to decide what
                          to offer to save. Giving them an empty pair to grab
                          (kept off-screen, unreachable by tab/click) keeps
                          them away from our real fields without touching the
                          real UX or values. This, plus autoComplete="off" on
                          the form and autoComplete="new-password" below, is
                          the standard combination sites use to stop the
                          native "Save password?" popup, browsers don't
                          expose a way to fully disable it otherwise. */}
                      <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0, pointerEvents: "none" }} aria-hidden="true">
                        <input type="text" name="fake-username" tabIndex={-1} autoComplete="off" defaultValue="" />
                        <input type="password" name="fake-password" tabIndex={-1} autoComplete="off" defaultValue="" />
                      </div>
                      <Field
                        label="Email or ID"
                        type="text"
                        autoComplete="off"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="e.g. employee@hr.com"
                      />
                      <Field
                        label="Password"
                        type="password"
                        autoComplete="new-password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="Enter your password"
                      />
                      <div className="flex items-center justify-between pt-0.5">
                        <SkeuCheckbox
                          id="hrms-remember-me"
                          checked={!!rememberMe}
                          onChange={(e) => setRememberMe?.(e.target.checked)}
                          label="Remember me"
                          accent={ACCENT}
                        />
                      </div>
                      <PrimaryButton type="submit" className="w-full">
                        Login
                      </PrimaryButton>
                    </form>
                    <p className="text-center text-sm mt-5" style={{ color: DS.grid }}>
                      Don't have an account?{" "}
                      <button
                        onClick={() => { setSignupRequestSent?.(false); setMode("signup"); }}
                        className="font-bold hover:underline"
                        style={{ color: ACCENT }}
                      >
                        Sign up
                      </button>
                    </p>
                    <p className="text-center text-sm mt-1.5" style={{ color: DS.grid }}>
                      Company uses invites?{" "}
                      <button
                        onClick={() => { setRequestSent(false); setMode("request"); }}
                        className="font-bold hover:underline"
                        style={{ color: ACCENT }}
                      >
                        Request access
                      </button>
                    </p>
                  </>
                )}

                {mode === "signup" && (
                  <>
                    <h2 className="mt-2 text-2xl font-extrabold" style={{ color: DS.forest }}>
                      Create account
                    </h2>
                    {signupRequestSent ? (
                      <div className="mt-6">
                        <StatusBadge color={ACCENT}>request sent</StatusBadge>
                        <p className="mt-3 text-sm" style={{ color: DS.grid }}>
                          Thanks! An administrator will review your request. Once approved,
                          you'll get an email with a link to set your password and sign in.
                        </p>
                        <button
                          onClick={goToSignin}
                          className="mt-5 font-bold text-sm hover:underline"
                          style={{ color: ACCENT }}
                        >
                          ← Back to sign in
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="mt-1.5 text-sm" style={{ color: DS.grid }}>
                          Submit your details and an admin will approve your access.
                          You'll be emailed a link to set your password once approved.
                        </p>
                        <form
                          className="mt-6 space-y-4"
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleSignupSubmit();
                          }}
                        >
                          <Field
                            label="Full name"
                            type="text"
                            value={signupUsername}
                            onChange={(e) => setSignupUsername(e.target.value)}
                            placeholder="e.g. John Doe"
                          />
                          <Field
                            label="Email"
                            type="email"
                            value={signupEmail}
                            onChange={(e) => setSignupEmail(e.target.value)}
                            placeholder="you@company.com"
                          />
                          <PrimaryButton type="submit" className="w-full">
                            Create account
                          </PrimaryButton>
                        </form>
                        <p className="text-center text-sm mt-5" style={{ color: DS.grid }}>
                          Already have an account?{" "}
                          <button
                            onClick={() => setMode("signin")}
                            className="font-bold hover:underline"
                            style={{ color: ACCENT }}
                          >
                            Sign in
                          </button>
                        </p>
                      </>
                    )}
                  </>
                )}

                {mode === "request" && (
                  <>
                    <h2 className="mt-2 text-2xl font-extrabold" style={{ color: DS.forest }}>
                      Request access
                    </h2>
                    {requestSent ? (
                      <div className="mt-6">
                        <StatusBadge color={ACCENT}>request sent</StatusBadge>
                        <p className="mt-3 text-sm" style={{ color: DS.grid }}>
                          Thanks! An administrator will review your request. Once approved,
                          you'll get an email with a link to set your password.
                        </p>
                        <button
                          onClick={() => setMode("signin")}
                          className="mt-5 font-bold text-sm hover:underline"
                          style={{ color: ACCENT }}
                        >
                          ← Back to sign in
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="mt-1.5 text-sm" style={{ color: DS.grid }}>
                          Ask your HR admin for access. You'll be emailed a setup link
                          once approved.
                        </p>
                        <form className="mt-6 space-y-4" onSubmit={submitRequest}>
                          <Field
                            label="Full name"
                            type="text"
                            value={requestName}
                            onChange={(e) => setRequestName(e.target.value)}
                            placeholder="e.g. John Doe"
                          />
                          <Field
                            label="Work email"
                            type="email"
                            value={requestEmail}
                            onChange={(e) => setRequestEmail(e.target.value)}
                            placeholder="you@company.com"
                          />
                          <PrimaryButton type="submit" className="w-full">
                            Request access
                          </PrimaryButton>
                        </form>
                        <p className="text-center text-sm mt-5" style={{ color: DS.grid }}>
                          Already have an account?{" "}
                          <button
                            onClick={() => setMode("signin")}
                            className="font-bold hover:underline"
                            style={{ color: ACCENT }}
                          >
                            Sign in
                          </button>
                        </p>
                      </>
                    )}
                  </>
                )}
              </BlueprintCard>
            </div>
          </div>
        </PageContainer>
      </Section>

      {/* TOOLS */}
      <Section style={{ background: "transparent" }}>
        <PageContainer>
          <SectionHeader
            badge="capabilities"
            title="Comprehensive HR management tools"
            subtitle="Everything an HR team needs, unified under one blueprint."
            meta="9 modules"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: DS.hairline }}>
            {featureTools.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="p-7 transition-colors hover:bg-[#1A3C2B]/[0.03]" style={{ background: DS.paper }}>
                <Icon className="w-7 h-7" strokeWidth={1.6} style={{ color: ACCENT }} />
                <h3 className="mt-4 font-bold text-lg" style={{ color: DS.forest }}>
                  {title}
                </h3>
                <p className="mt-1.5 text-sm" style={{ color: DS.grid }}>
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </PageContainer>
      </Section>

      {/* RESULTS */}
      <Section style={{ background: "transparent" }}>
        <PageContainer>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <SectionHeader
                badge="outcomes"
                title="Proven results for growing teams"
                className="!mb-6"
              />
              <ul className="space-y-4">
                {[
                  ["75%", "Reduction in administrative workload"],
                  ["99.9%", "Compliance accuracy"],
                  ["45%", "Improvement in employee satisfaction"],
                ].map(([stat, label]) => (
                  <li key={label} className="flex items-center gap-4">
                    <span className="text-3xl font-extrabold tabular-nums" style={{ color: ACCENT }}>
                      {stat}
                    </span>
                    <span className="text-sm" style={{ color: DS.grid }}>
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <BlueprintCard accent={ACCENT}>
              <TechLabel style={{ color: `${DS.grid}99` }}>employee satisfaction · trend</TechLabel>
              <div className="relative h-64 mt-3">
                <svg viewBox="0 0 400 200" className="w-full h-full">
                  <polyline
                    points="20,180 100,160 180,140 260,120 340,90"
                    fill="none"
                    stroke={ACCENT}
                    strokeWidth="2.5"
                  />
                  {[[20, 180], [100, 160], [180, 140], [260, 120], [340, 90]].map(([x, y]) => (
                    <rect key={`${x}-${y}`} x={x - 3} y={y - 3} width="6" height="6" fill={ACCENT} />
                  ))}
                  <line x1="0" y1="190" x2="400" y2="190" stroke={DS.hairline} strokeWidth="1" />
                  <text x="34" y="200" fontSize="11" fill={DS.grid}>2021</text>
                  <text x="170" y="200" fontSize="11" fill={DS.grid}>2023</text>
                  <text x="316" y="200" fontSize="11" fill={DS.grid}>2025</text>
                </svg>
              </div>
            </BlueprintCard>
          </div>
        </PageContainer>
      </Section>

      {/* AI FEATURES */}
      <Section style={{ background: "transparent" }}>
        <PageContainer>
          <SectionHeader
            badge="intelligence"
            title="Powered by advanced AI"
            subtitle="Automate the busywork so your team can focus on people."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "AI attendance predictions",
              "Auto-schedule interviews",
              "AI-powered feedback",
              "Smart leave requests",
              "Auto-generated letters",
              "Predictive analytics",
            ].map((title) => (
              <div key={title} className="border-l-2 pl-4 py-1" style={{ borderColor: ACCENT }}>
                <h3 className="font-bold" style={{ color: DS.forest }}>
                  {title}
                </h3>
                <p className="mt-1 text-sm" style={{ color: DS.grid }}>
                  Leverage AI to automate and enhance your HR processes.
                </p>
              </div>
            ))}
          </div>
        </PageContainer>
      </Section>

      {/* NEWSLETTER */}
      <Section dark className="!border-b-0">
        <PageContainer>
          <div className="max-w-2xl mx-auto text-center">
            <StatusBadge color={DS.mint}>stay updated</StatusBadge>
            <h2 className="mt-4 text-4xl font-bold" style={{ color: DS.paper }}>
              Product updates, straight to your inbox
            </h2>
            <p className="mt-3" style={{ color: "rgba(247,247,245,0.75)" }}>
              Monthly feature rollouts, HR tips and more.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <SkeuInput
                type="email"
                placeholder="Enter your email"
                className="sm:w-80 !bg-white"
              />
              <PrimaryButton type="button">Subscribe</PrimaryButton>
            </div>
          </div>
        </PageContainer>
      </Section>

      {/* FOOTER */}
      <footer style={{ background: DS.forest, color: DS.paper }} className="px-6 md:px-10 py-14">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            ["Product", ["Features", "Demo", "Integrations", "Pricing"]],
            ["Company", ["About", "Careers", "Press", "Contact"]],
            ["Support", ["Help Center", "Documentation", "API Status", "Contact Support"]],
            ["Legal", ["Terms", "Privacy", "Security", "Compliance"]],
          ].map(([heading, links]) => (
            <div key={heading}>
              <TechLabel style={{ color: DS.mint }}>{heading}</TechLabel>
              <ul className="mt-4 space-y-2 text-sm" style={{ color: "rgba(247,247,245,0.7)" }}>
                {links.map((l) => (
                  <li key={l}>
                    <button className="hover:text-white transition-colors">{l}</button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </footer>
    </div>
  );
}